/**
 * Phase 101ga (Feb 2026) — Vague 4 : panneau de détection & fusion des doublons.
 *
 * Ouvert depuis Parties.jsx via le bouton "Détecter doublons".
 * Liste les groupes de Parties partageant un même dedup_key (email ou
 * nom slugifié), avec un bouton "Fusionner" par paire.
 *
 * UX :
 *  - Chaque groupe est une carte avec le dedup_key en tête (chip doré)
 *  - Les Parties du groupe sont listées avec leur display_name, email,
 *    ville, et nombre de sources liées
 *  - "Choisir comme cible" sur une Party = celle-ci devient la cible ; les
 *    autres sont proposées à la fusion
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users2, X, Loader2, RefreshCw, GitMerge, Building2, User, Target,
  ArrowRight, AlertTriangle,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

export function DuplicatesPanel({ open, onClose, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState({}); // {group_key: party_id}
  const [merging, setMerging] = useState({}); // {`${target}|${source}`: bool}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/parties/duplicates/list");
      setGroups(data.groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur détection doublons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const mergeInto = async (groupKey, targetId, sourceId, sourceName) => {
    const key = `${targetId}|${sourceId}`;
    setMerging((m) => ({ ...m, [key]: true }));
    try {
      const { data } = await api.post(
        `/admin/parties/${targetId}/merge`,
        { source_id: sourceId, fill_from_source: true }
      );
      toast.success(
        `✅ « ${sourceName} » fusionnée · ${data.sources_moved} source(s) déplacée(s), ${data.backrefs_rewritten} lien(s) réécrit(s)`,
        { duration: 6000 }
      );
      onMerged?.(data);
      // Recharge les groupes (le doublon absorbé disparaît)
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur fusion");
    } finally {
      setMerging((m) => ({ ...m, [key]: false }));
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="parties-duplicates-panel"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users2 className="w-5 h-5 text-[#1F4E3D]" />
            Doublons potentiels
          </SheetTitle>
          <div className="text-xs text-[#575A57] pl-7">
            Groupes de Parties partageant un même email ou nom normalisé.
            Choisis une cible dans chaque groupe puis fusionne les autres
            entrées dedans (fill_if_empty · aucun écrasement).
          </div>
        </SheetHeader>

        <div className="mt-3 flex justify-between items-center">
          <div className="text-xs text-[#575A57]">
            {loading ? "Détection…" : `${groups.length} groupe(s) détecté(s)`}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="duplicates-refresh-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Rescanner
          </Button>
        </div>

        {!loading && groups.length === 0 && (
          <div className="mt-6 p-4 rounded-md bg-[#F0F7F2] border border-[#B7D0BF] text-sm text-[#1F4E3D]">
            🎉 Aucun doublon détecté. Le carnet est propre.
          </div>
        )}

        <div className="mt-3 space-y-3">
          {groups.map((g) => {
            const targetId = selectedTarget[g.dedup_key] || g.parties[0].id;
            const target = g.parties.find((p) => p.id === targetId) || g.parties[0];
            const sources = g.parties.filter((p) => p.id !== target.id);
            return (
              <div
                key={g.dedup_key}
                className="border border-[#E5E5E0] rounded-md p-3 bg-white"
                data-testid={`duplicate-group-${g.dedup_key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {g.dedup_key}
                  </Badge>
                  <span className="text-[10px] text-[#A3A6A3]">
                    {g.count} entrée(s)
                  </span>
                </div>

                {/* Sélection cible */}
                <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1">
                  Cible (garde son ID)
                </div>
                <div className="grid gap-1 mb-3">
                  {g.parties.map((p) => (
                    <label
                      key={p.id}
                      className={`flex items-center gap-2 p-2 rounded border text-sm cursor-pointer ${p.id === target.id ? "border-[#1F4E3D] bg-[#F0F7F2]" : "border-[#E5E5E0] bg-[#FAFAF7] hover:bg-[#F5F5F0]"}`}
                    >
                      <input
                        type="radio"
                        name={`target-${g.dedup_key}`}
                        checked={p.id === target.id}
                        onChange={() => setSelectedTarget((prev) => ({
                          ...prev, [g.dedup_key]: p.id,
                        }))}
                        className="accent-[#1F4E3D]"
                        data-testid={`target-radio-${p.id}`}
                      />
                      {p.kind === "organization"
                        ? <Building2 className="w-3 h-3 text-[#575A57]" />
                        : <User className="w-3 h-3 text-[#575A57]" />}
                      <div className="flex-1">
                        <div className="font-medium">{p.display_name}</div>
                        <div className="text-[10px] text-[#A3A6A3]">
                          {p.email || "—"} · {p.city || "sans ville"} · {p.linked_sources_count} source(s)
                        </div>
                      </div>
                      {p.id === target.id && (
                        <Target className="w-3 h-3 text-[#1F4E3D]" />
                      )}
                    </label>
                  ))}
                </div>

                {/* Actions fusion */}
                {sources.length > 0 && (
                  <div className="border-t border-[#E5E5E0] pt-2 space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-[#8C3A2A] mb-1">
                      À absorber dans la cible
                    </div>
                    {sources.map((s) => {
                      const key = `${target.id}|${s.id}`;
                      const isMerging = merging[key];
                      return (
                        <div
                          key={s.id}
                          className="flex items-center justify-between gap-2 text-xs bg-[#FDF3EA] border border-[#F4CBB0] rounded p-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium truncate">
                              {s.display_name}
                            </span>
                            <ArrowRight className="w-3 h-3 text-[#8C6B2F]" />
                            <span className="text-[#1F4E3D] truncate">
                              {target.display_name}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#F0F7F2] shrink-0"
                            onClick={() => mergeInto(g.dedup_key, target.id, s.id, s.display_name)}
                            disabled={isMerging}
                            data-testid={`merge-btn-${s.id}`}
                          >
                            {isMerging
                              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              : <GitMerge className="w-3 h-3 mr-1" />}
                            Fusionner
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {groups.length > 0 && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-[#FBF4E9] border border-[#E9D9B7] text-xs text-[#8C6B2F]">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>Stratégie fill_if_empty.</strong> Les champs vides de la
              cible sont remplis depuis la source ; les valeurs existantes ne
              sont jamais écrasées. Les liens (party_id) sont ré-attachés
              atomiquement.
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
